/*
 * Copyright (c) 2025 Sila Artificial Intelligence Research LLC
 * A DA Holding Ltd Company
 * All rights reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 * This software and its documentation are proprietary to Sila Artificial
 * Intelligence Research LLC. Unauthorized copying, distribution, or use
 * of this software is strictly prohibited.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL
 * THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING
 * FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER
 * DEALINGS IN THE SOFTWARE.
 */

import React from 'react';

interface AIInsightPanelProps {
  title?: string;
  insights: string[] | null;
  type?: 'default' | 'predictions' | 'segments' | 'opportunities' | 'growth';
  loading?: boolean;
}

const AIInsightPanel: React.FC<AIInsightPanelProps> = ({
  title = 'AI Insights',
  insights,
  type = 'default',
  loading = false
}) => {
  const getTypeStyles = () => {
    switch (type) {
      case 'predictions':
        return {
          border: 'border-green-500/20',
          bg: 'bg-green-500/5',
          icon: 'fa-chart-line',
          iconColor: 'text-green-600 dark:text-green-400'
        };
      case 'segments':
        return {
          border: 'border-orange-500/20',
          bg: 'bg-orange-500/5',
          icon: 'fa-users',
          iconColor: 'text-orange-600 dark:text-orange-400'
        };
      case 'opportunities':
        return {
          border: 'border-purple-500/20',
          bg: 'bg-purple-500/5',
          icon: 'fa-rocket',
          iconColor: 'text-purple-600 dark:text-purple-400'
        };
      case 'growth':
        return {
          border: 'border-yellow-500/20',
          bg: 'bg-yellow-500/5',
          icon: 'fa-seedling',
          iconColor: 'text-yellow-600 dark:text-yellow-400'
        };
      default:
        return {
          border: 'border-primary/20',
          bg: 'bg-primary/5',
          icon: 'fa-brain',
          iconColor: 'text-primary'
        };
    }
  };

  const styles = getTypeStyles();

  if (loading) {
    return (
      <div className={`bg-card rounded-xl p-5 border ${styles.border}`}>
        <div className="flex items-center space-x-3 mb-4">
          <i className={`fas ${styles.icon} ${styles.iconColor} animate-pulse`}></i>
          <h4 className="text-lg font-semibold text-foreground">{title}</h4>
        </div>
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="flex items-start space-x-3">
              <div className="w-2 h-2 bg-background-tertiary rounded-full mt-2 animate-pulse"></div>
              <div className="flex-1">
                <div className="h-4 bg-background-tertiary rounded animate-pulse"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!insights || insights.length === 0) {
    return (
      <div className={`bg-card rounded-xl p-5 border ${styles.border}`}>
        <div className="flex items-center space-x-3 mb-4">
          <i className={`fas ${styles.icon} ${styles.iconColor}`}></i>
          <h4 className="text-lg font-semibold text-foreground">{title}</h4>
        </div>
        <p className="text-text-muted text-center py-4">No insights available</p>
      </div>
    );
  }

  return (
    <div className={`bg-card rounded-xl p-5 border ${styles.border} mt-4`}>
      <div className="flex items-center space-x-3 mb-4">
        <i className={`fas ${styles.icon} ${styles.iconColor}`}></i>
        <h4 className="text-lg font-semibold text-foreground">{title}</h4>
      </div>

      <div className="space-y-3">
        {insights.map((insight, index) => (
          <div key={index} className={`flex items-start space-x-3 p-3 rounded-lg ${styles.bg}`}>
            <div className="flex-shrink-0">
              <div className={`w-2 h-2 rounded-full mt-2 ${
                type === 'predictions' ? 'bg-green-500' :
                type === 'segments' ? 'bg-orange-500' :
                type === 'opportunities' ? 'bg-purple-500' :
                type === 'growth' ? 'bg-yellow-500' :
                'bg-primary'
              }`}></div>
            </div>
            <p className="text-text-secondary text-sm leading-relaxed">{insight}</p>
          </div>
        ))}
      </div>

      {/* Optional action buttons based on type */}
      {type === 'opportunities' && (
        <div className="mt-4 pt-4 border-t border-border">
          <button className="text-purple-600 dark:text-purple-400 hover:text-purple-500 text-sm font-medium transition-colors">
            <i className="fas fa-external-link-alt mr-2"></i>
            View detailed analysis
          </button>
        </div>
      )}

      {type === 'predictions' && (
        <div className="mt-4 pt-4 border-t border-border">
          <div className="flex items-center justify-between text-xs text-text-muted">
            <span>
              <i className="fas fa-clock mr-1"></i>
              Updated: Just now
            </span>
            <span>
              <i className="fas fa-check-circle mr-1 text-green-600 dark:text-green-400"></i>
              High confidence
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIInsightPanel;
